import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ThumbsUp, MessageCircle, Share2, MoreHorizontal, Play, Pause,
  Music, Plus, Flag, EyeOff, Bookmark, ToggleLeft, ToggleRight, X, Send,
  Loader2, Clapperboard, Compass, Disc3, Lock, Globe, Volume2, VolumeX,
} from "lucide-react";
import { api, API } from "@/api/client";
import { pipelinesApi } from "@/api/mist";
import { socialApi } from "@/api/social";
import { useSession } from "@/context/SessionContext";

const CORAL = "#FF4D4D";

/* ------------------------------ dock button ------------------------------ */
function DockButton({ icon: Icon, count, onClick, active, testid, filled }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={onClick}
        data-testid={testid}
        className="w-12 h-12 rounded-2xl grid place-items-center border-2 border-black bg-white/10 text-white backdrop-blur-sm shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow,background-color] duration-150"
        style={active ? { backgroundColor: CORAL, color: "#fff" } : undefined}
      >
        <Icon size={22} strokeWidth={2.3} fill={filled ? "currentColor" : "none"} />
      </button>
      {count != null && (
        <span className="font-mono text-[10px] font-bold text-white drop-shadow" data-testid={`${testid}-count`}>{count}</span>
      )}
    </div>
  );
}

/* ------------------------------- comments -------------------------------- */
function CommentDrawer({ open, onClose, reelId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    socialApi.comments(reelId, "reel")
      .then((r) => setComments(r.comments || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [open, reelId]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      // TODO: DB Linkage to mingle/misteck_preview cluster (social_comments)
      const r = await socialApi.addComment(reelId, text.trim(), "reel");
      setComments((c) => [...c, r.comment]);
      setText("");
    } catch { toast.error("Could not comment"); }
  };

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-40 max-w-[412px] mx-auto transition-transform duration-300 ease-out ${open ? "translate-y-0" : "translate-y-full"}`}
      data-testid="reel-comment-drawer"
    >
      <div className="bg-cream border-t-4 border-black rounded-t-3xl h-[60dvh] flex flex-col shadow-[0_-6px_0px_#000]">
        <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-black/10">
          <MessageCircle size={16} className="text-[#FF4D4D]" />
          <h3 className="font-serif text-lg text-charcoal flex-1">Comments</h3>
          <button onClick={onClose} data-testid="reel-comment-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-black bg-white shadow-[2px_2px_0px_#000]"><X size={15} /></button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 size={18} className="text-[#FF4D4D] animate-spin" /></div>
          ) : comments.length === 0 ? (
            <p className="font-mono text-[11px] text-charcoal/40 text-center py-6">Be the first to comment</p>
          ) : comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2 bg-white border-2 border-black rounded-xl p-2 shadow-[2px_2px_0px_#000]">
              <div className="w-8 h-8 rounded-full border-2 border-black bg-gold grid place-items-center shrink-0 font-mono text-[10px] font-black text-black">{(c.author_name || "?").slice(0, 2).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[10px] font-bold text-charcoal">{c.author_name}</p>
                <p className="font-mono text-[11px] text-charcoal/75 break-words">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-4 py-3 border-t-2 border-black/10">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Add a comment…"
            data-testid="reel-comment-input"
            className="flex-1 bg-white border-2 border-black rounded-xl px-3 py-2 font-mono text-[12px] text-charcoal outline-none focus:ring-2 focus:ring-[#FF4D4D] shadow-[2px_2px_0px_#000]"
          />
          <button onClick={send} data-testid="reel-comment-send" className="w-10 h-10 grid place-items-center rounded-xl border-2 border-black text-white shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none" style={{ backgroundColor: CORAL }}><Send size={15} /></button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ options sheet ---------------------------- */
function OptionsSheet({ open, onClose, autoPlay, onToggleAutoPlay, autoScroll, onToggleAutoScroll,
  onHide, onReport, onSave, isOwner, published, onPublish, onUnpublish, publishing }) {
  const rows = [
    { label: "Hide", icon: EyeOff, fn: onHide, testid: "reel-opt-hide" },
    { label: "Report", icon: Flag, fn: onReport, testid: "reel-opt-report", danger: true },
    { label: "Save Post", icon: Bookmark, fn: onSave, testid: "reel-opt-save" },
  ];
  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-40 max-w-[412px] mx-auto transition-transform duration-300 ease-out ${open ? "translate-y-0" : "translate-y-full"}`}
      data-testid="reel-options-sheet"
    >
      <div className="bg-cream border-t-4 border-black rounded-t-3xl p-4 shadow-[0_-6px_0px_#000] space-y-2">
        <div className="flex items-center gap-2 pb-2 border-b-2 border-black/10">
          <MoreHorizontal size={16} className="text-[#FF4D4D]" />
          <h3 className="font-serif text-lg text-charcoal flex-1">Options</h3>
          <button onClick={onClose} data-testid="reel-options-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-black bg-white shadow-[2px_2px_0px_#000]"><X size={15} /></button>
        </div>

        {/* Owner-only publish control (high-contrast) */}
        {isOwner && (
          published ? (
            <button onClick={onUnpublish} disabled={publishing} data-testid="reel-opt-unpublish"
              className="w-full flex items-center gap-3 bg-charcoal text-white border-2 border-black rounded-xl px-3 py-3 font-mono text-[13px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Make Private (remove from Explore)
            </button>
          ) : (
            <button onClick={onPublish} disabled={publishing} data-testid="reel-opt-publish"
              className="w-full flex items-center gap-3 border-2 border-black rounded-xl px-3 py-3 font-mono text-[13px] font-black uppercase tracking-wider text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60"
              style={{ backgroundColor: "#3BE07A" }}>
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />} Publish to Explore Feed
            </button>
          )
        )}

        {rows.map((r) => (
          <button key={r.label} onClick={r.fn} data-testid={r.testid}
            className={`w-full flex items-center gap-3 bg-white border-2 border-black rounded-xl px-3 py-3 font-mono text-[12px] font-bold uppercase tracking-wider shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${r.danger ? "text-red-600" : "text-charcoal"}`}>
            <r.icon size={16} /> {r.label}
          </button>
        ))}
        <button onClick={onToggleAutoPlay} data-testid="reel-opt-autoplay"
          className="w-full flex items-center gap-3 bg-white border-2 border-black rounded-xl px-3 py-3 font-mono text-[12px] font-bold uppercase tracking-wider text-charcoal shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
          {autoPlay ? <ToggleRight size={18} className="text-[#FF4D4D]" /> : <ToggleLeft size={18} className="text-charcoal/40" />}
          Auto-Play {autoPlay ? "On" : "Off"}
        </button>
        <button onClick={onToggleAutoScroll} data-testid="reel-opt-autoscroll"
          className="w-full flex items-center gap-3 bg-white border-2 border-black rounded-xl px-3 py-3 font-mono text-[12px] font-bold uppercase tracking-wider text-charcoal shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
          {autoScroll ? <ToggleRight size={18} className="text-[#FF4D4D]" /> : <ToggleLeft size={18} className="text-charcoal/40" />}
          Auto-Scroll {autoScroll ? "On" : "Off"}
        </button>
      </div>
    </div>
  );
}

/* --------------------------------- slide --------------------------------- */
function Slide({ reel, onHide, onOpenSound, onPublishedChange, autoScroll, onToggleAutoScroll, isActive, onContinueDraft, onUnlock }) {
  const videoRef = useRef(null);
  const clickTimeout = useRef(null);

  const [liked, setLiked] = useState(!!reel.liked);
  const [likes, setLikes] = useState(reel.likes || 0);
  const [following, setFollowing] = useState(false);
  const [heartBurst, setHeartBurst] = useState(false);
  const [tapIcon, setTapIcon] = useState(null); // 'play' | 'pause' | null
  const [autoPlay, setAutoPlay] = useState(true);
  const [muted, setMuted] = useState(false);   // reels play with SOUND ON by default
  const [commentOpen, setCommentOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [published, setPublished] = useState(!!reel.published);
  const [publishing, setPublishing] = useState(false);
  const [mediaBroken, setMediaBroken] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const doUnlock = async () => {
    setUnlocking(true);
    try {
      await pipelinesApi.dmReelsUnlock(reel.id);
      toast.success("Unlocked 🔓");
      onUnlock?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || "Couldn't unlock — check your Credits balance");
    } finally { setUnlocking(false); }
  };
  const isOwner = !!reel.is_owner;
  const showPlaceholder = reel.needsRender || mediaBroken;

  useEffect(() => () => clickTimeout.current && clearTimeout(clickTimeout.current), []);

  // Viewport gating — only the active (in-view) slide is allowed to play/decode;
  // off-screen slides pause immediately so N reels never spin up N decoders.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive && autoPlay) {
      v.muted = muted;
      v.play().catch(() => {
        // Browser blocked unmuted autoplay → fall back to muted + tap-to-unmute.
        if (!v.muted) { v.muted = true; setMuted(true); v.play().catch(() => {}); }
      });
    } else { v.pause(); }
  }, [isActive, autoPlay, muted]);

  const toggleMute = () => {
    const v = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (v) { v.muted = next; if (isActive && autoPlay) v.play().catch(() => {}); }
  };

  // Advance to the next reel on playback end when auto-scroll is enabled.
  const handleEnded = () => {
    if (!autoScroll) return;
    const cur = document.getElementById(`slide-${reel.id}`);
    const next = cur?.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    else if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      await api.post("/central-core/query_vault", { op: "publish_reel", params: { asset_id: reel.id } });
      setPublished(true); onPublishedChange?.(reel.id, true); setOptionsOpen(false);
      toast.success("Published to Explore Feed 🌐");
    } catch { toast.error("Could not publish"); } finally { setPublishing(false); }
  };
  const doUnpublish = async () => {
    setPublishing(true);
    try {
      await api.post("/central-core/query_vault", { op: "unpublish_reel", params: { asset_id: reel.id } });
      setPublished(false); onPublishedChange?.(reel.id, false); setOptionsOpen(false);
      toast("Now a private draft 🔒", { icon: "🔒" });
    } catch { toast.error("Could not make private"); } finally { setPublishing(false); }
  };


  // ----- backend-linked handlers (instant local feedback) -----
  const handleLikeToggle = async () => {
    const prevLiked = liked, prevLikes = likes;
    const next = !liked;
    setLiked(next);
    setLikes((n) => (next ? n + 1 : Math.max(0, n - 1)));
    try {
      // TODO: DB Linkage to mingle/misteck_preview cluster (social_likes)
      const r = await socialApi.like(reel.id, "reel");
      setLiked(r.liked);
      setLikes(r.likes);
    } catch {
      setLiked(prevLiked); setLikes(prevLikes);
      toast.error("Could not react");
    }
  };

  const triggerBurst = () => {
    setHeartBurst(true);
    setTimeout(() => setHeartBurst(false), 600);
  };

  const likeFromDoubleTap = async () => {
    triggerBurst();
    if (!liked) await handleLikeToggle(); // like-only, never unlike on dbl-tap
  };

  const handleFollow = async (creatorId) => {
    setFollowing(true); // smooth-fade the + out on activation
    try {
      // TODO: DB Linkage to mingle/misteck_preview cluster (social_relations follow)
      await socialApi.follow(creatorId);
      toast.success(`Following @${reel.handle}`);
    } catch {
      toast(`Follow queued for @${reel.handle}`, { icon: "➕" });
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/#/reel/${reel.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: reel.title, text: reel.caption, url }); }
      catch { /* user dismissed the native share sheet — no error toast */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Couldn't copy link"); }
  };

  const handleHide = async () => {
    setOptionsOpen(false);
    try {
      // TODO: DB Linkage to mingle/misteck_preview cluster (is_hidden flag)
      await socialApi.hideItem(reel.id, "reel");
    } catch { /* still hide locally */ }
    onHide?.(reel.id);
    toast("Hidden", { icon: "🙈" });
  };

  // ----- gesture engine (single vs double, race-safe via clickTimeout) -----
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setTapIcon("play"); }
    else { v.pause(); setTapIcon("pause"); }
    setTimeout(() => setTapIcon(null), 480);
  };

  const onSurfaceClick = () => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
      likeFromDoubleTap();
      return;
    }
    clickTimeout.current = setTimeout(() => {
      togglePlay();
      clickTimeout.current = null;
    }, 250);
  };

  const toggleAutoPlay = () => {
    setAutoPlay((a) => {
      const next = !a;
      const v = videoRef.current;
      if (v) { next ? v.play().catch(() => {}) : v.pause(); }
      return next;
    });
  };

  return (
    <div id={`slide-${reel.id}`} data-slide-id={reel.id} className="relative h-full w-full snap-start shrink-0 bg-black overflow-hidden flex items-center justify-center" data-testid="reel-slide">
      {reel.locked ? (
        <div className="absolute inset-0 grid place-items-center bg-black" data-testid={`reel-locked-${reel.id}`}>
          {reel.poster && <img src={reel.poster} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40" />}
          <div className="relative z-10 w-full max-w-[300px] mx-4 border-2 border-[#FFE08A] rounded-2xl bg-black/80 p-6 text-center backdrop-blur">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFE08A] border-2 border-black grid place-items-center shadow-[3px_3px_0px_#000]"><Lock size={24} className="text-black" /></div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Premium reel</p>
            <p className="mt-1 font-serif text-lg text-white">{reel.caption || "Unlock to watch"}</p>
            <button onClick={doUnlock} disabled={unlocking} data-testid={`reel-unlock-${reel.id}`}
              className="mt-4 inline-flex items-center gap-2 font-mono text-xs font-black uppercase tracking-widest text-black bg-[#FFE08A] border-2 border-black rounded-full px-5 py-2.5 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              {unlocking ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Unlock · {reel.credit_cost || 5} Credits
            </button>
          </div>
        </div>
      ) : showPlaceholder ? (
        <button
          onClick={() => onContinueDraft?.(reel)}
          data-testid={`reel-draft-placeholder-${reel.id}`}
          className="absolute inset-0 grid place-items-center bg-charcoal px-6"
        >
          <div className="w-full max-w-[300px] border-2 border-black rounded-2xl bg-white p-6 text-center shadow-[6px_6px_0px_#000]">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FF4D4D] border-2 border-black grid place-items-center shadow-[3px_3px_0px_#000]">
              <Clapperboard size={26} className="text-white" strokeWidth={2.4} />
            </div>
            <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-charcoal/50">Unrendered</p>
            <p className="mt-1 font-mono text-base font-black uppercase tracking-wide text-charcoal leading-tight">DRAFT — READY TO RENDER</p>
            <p className="mt-2 font-mono text-[11px] text-charcoal/60 leading-snug">This reel hasn't been rendered yet. Finish it in the studio to publish.</p>
            <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs font-black uppercase tracking-widest text-white bg-charcoal border-2 border-black rounded-full px-4 py-2 shadow-[3px_3px_0px_#000]">
              <Play size={14} fill="currentColor" /> Finish in studio
            </span>
          </div>
        </button>
      ) : reel.src ? (
        <video
          ref={videoRef}
          src={reel.src}
          muted={muted}
          loop={!autoScroll}
          playsInline
          preload={isActive ? "auto" : "none"}
          poster={reel.poster}
          onEnded={handleEnded}
          onError={() => setMediaBroken(true)}
          onClick={onSurfaceClick}
          data-testid={`reel-video-${reel.id}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div onClick={onSurfaceClick} className="absolute inset-0 grid place-items-center text-white/40 font-mono text-xs" data-testid={`reel-loading-${reel.id}`}><Loader2 size={22} className="animate-spin text-white/50" /></div>
      )}

      {/* legibility gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/50 pointer-events-none" />

      {/* center tap play/pause overlay */}
      {tapIcon && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none z-30" data-testid="reel-tap-overlay">
          <div className="w-20 h-20 rounded-full bg-black/50 border-2 border-white/70 grid place-items-center animate-tap-fade">
            {tapIcon === "play" ? <Play size={38} className="text-white" fill="currentColor" /> : <Pause size={38} className="text-white" fill="currentColor" />}
          </div>
        </div>
      )}

      {/* double-tap heart burst */}
      {heartBurst && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none z-30" data-testid="reel-heart-burst">
          <ThumbsUp size={120} fill={CORAL} color={CORAL} className="animate-heart-burst drop-shadow-[0_0_20px_rgba(255,77,77,0.8)]" />
        </div>
      )}

      {/* Status badge — only for the owner's own reels */}
      {isOwner && (
        published ? (
          <span data-testid={`reel-badge-published-${reel.id}`} className="absolute top-16 left-4 z-20 flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-widest rounded-full bg-[#3BE07A] text-black border-2 border-black px-3 py-1 shadow-[2px_2px_0px_#000]">
            <Globe size={12} strokeWidth={2.6} /> Published to Explore
          </span>
        ) : (
          <span data-testid={`reel-badge-draft-${reel.id}`} className="absolute top-16 left-4 z-20 flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-widest rounded-full bg-white text-charcoal border-2 border-black px-3 py-1 shadow-[2px_2px_0px_#000]">
            <Lock size={12} strokeWidth={2.6} /> Private Draft
          </span>
        )
      )}

      {/* Mute / unmute — reels default to SOUND ON; if the browser blocks unmuted
          autoplay it falls back to muted and this button becomes tap-to-unmute. */}
      {!showPlaceholder && reel.src && (
        <button onClick={toggleMute} data-testid={`reel-mute-${reel.id}`} aria-label={muted ? "Unmute" : "Mute"}
          className="absolute top-16 right-4 z-30 w-10 h-10 rounded-full bg-black/55 border-2 border-white/70 grid place-items-center text-white active:scale-90 shadow-[2px_2px_0px_#000]">
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}

      {/* ===== RIGHT VERTICAL DOCK (strict order) ===== */}
      <div className="absolute right-3 bottom-28 z-20 flex flex-col items-center gap-4" data-testid="reel-controls">
        {/* 1 · Avatar + Follow node */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-2 border-black bg-neutral-800 overflow-hidden grid place-items-center text-white font-mono text-sm shadow-[3px_3px_0px_#000]" data-testid="reel-avatar">
              {reel.author_avatar ? <img src={reel.author_avatar} alt="" className="w-full h-full object-cover" /> : (reel.handle?.[0]?.toUpperCase() || "M")}
            </div>
            <button
              onClick={() => handleFollow(reel.creator_id || reel.handle)}
              data-testid="reel-follow-btn"
              className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full grid place-items-center border-2 border-black text-white shadow-[2px_2px_0px_#000] transition-opacity duration-300 ${following ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              style={{ backgroundColor: CORAL }}
              aria-label="Follow"
            >
              <Plus size={14} strokeWidth={3} />
            </button>
          </div>
        </div>

        {/* 2 · Like */}
        <DockButton icon={ThumbsUp} count={likes} active={liked} filled={liked} onClick={handleLikeToggle} testid="reel-like-btn" />

        {/* 3 · Comment */}
        <DockButton icon={MessageCircle} count={reel.comments || 0} onClick={() => setCommentOpen(true)} testid="reel-comment-btn" />

        {/* 4 · Share */}
        <DockButton icon={Share2} count={null} onClick={handleShare} testid="reel-share-btn" />

        {/* 5 · More options */}
        <DockButton icon={MoreHorizontal} count={null} onClick={() => setOptionsOpen(true)} testid="reel-options-btn" />

        {/* 6 · Spinning vinyl disc */}
        <button onClick={() => onOpenSound(reel.sound_id || reel.id)} data-testid="reel-vinyl-btn" aria-label="Sound" className="active:scale-90 transition-transform">
          <div className="w-10 h-10 rounded-full border-2 border-black bg-neutral-800 animate-spin grid place-items-center shadow-[2px_2px_0px_#000] overflow-hidden" style={{ animationDuration: "3s" }}>
            {reel.author_avatar
              ? <img src={reel.author_avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-white/40" />
              : <Disc3 size={18} className="text-white/70" />}
          </div>
        </button>
      </div>

      {/* ===== BOTTOM-LEFT GRAPHIC LAYER ===== */}
      <div className="absolute left-4 right-20 bottom-24 z-20" data-testid="reel-info">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-xs text-white/90 font-bold">@{reel.handle}</span>
        </div>
        <span className="inline-block bg-white text-charcoal font-serif text-lg px-2 py-0.5 mb-1.5 border-2 border-black">{reel.title}</span>
        <p className="font-mono text-[12px] text-white/85 leading-snug mb-2">{reel.caption}</p>

        {/* rolling music track ticker */}
        <button onClick={() => onOpenSound(reel.sound_id || reel.id)} data-testid="reel-music-ticker" className="flex items-center gap-2 max-w-full overflow-hidden bg-black/50 border-2 border-black rounded-full pl-2 pr-3 py-1 shadow-[2px_2px_0px_#000]">
          <Music size={13} className="text-[#FF4D4D] shrink-0" />
          <div className="overflow-hidden whitespace-nowrap">
            <span className="inline-block font-mono text-[10px] text-white/90 animate-[marquee_9s_linear_infinite]">
              🎵 Original Sound · @{reel.handle} — {reel.title} · Tap to explore the Sound Node
            </span>
          </div>
        </button>
      </div>

      <CommentDrawer open={commentOpen} onClose={() => setCommentOpen(false)} reelId={reel.id} />
      <OptionsSheet
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        autoPlay={autoPlay}
        onToggleAutoPlay={toggleAutoPlay}
        autoScroll={autoScroll}
        onToggleAutoScroll={onToggleAutoScroll}
        isOwner={isOwner}
        published={published}
        publishing={publishing}
        onPublish={doPublish}
        onUnpublish={doUnpublish}
        onHide={handleHide}
        onReport={() => { setOptionsOpen(false); toast("Report submitted for review", { icon: "🚩" }); }}
        onSave={() => { setOptionsOpen(false); toast.success("Saved to your collection"); }}
      />
    </div>
  );
}

/* -------------------------------- feed ----------------------------------- */
export default function ReelsFeed({ onBack, onMakeReel, scope = "global" }) {
  const [reels, setReels] = useState([]);
  const [tab, setTab] = useState("stream");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [targetId, setTargetId] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const scrollRef = useRef(null);
  const loadScope = tab === "mine" ? "personal" : scope;  // "Mine" tab shows the owner's own drafts + published

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/central-core/query_vault", { op: "stream_feed", params: { scope: loadScope } });
        if (cancelled) return;
        // Native progressive streaming: point <video src> straight at the authed
        // media endpoint with the JWT in the URL (Range-seekable, no full download).
        const authHeader = api.defaults.headers.common["Authorization"] || "";
        const tok = encodeURIComponent(authHeader.replace(/^Bearer\s+/i, ""));
        const list = (data.reels || []).map((r) => {
          const path = (r.stream_url || `/api/central-core/vault-file/${r.id}`).replace(/^\/api/, "");
          return {
            ...r,
            src: `${API}${path}?token=${tok}`,
            poster: `${API}/central-core/reel-poster/${r.id}?token=${tok}`,
          };
        });
        setReels(list);
      } catch (e) {
        if (!cancelled) toast.error("Could not load feed");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [loadScope, reloadKey]);

  // Reflect publish/unpublish locally; drop unpublished clips from the public Explore stream.
  const handlePublishedChange = (id, published) => setReels((rs) => {
    if (loadScope === "global" && !published) return rs.filter((x) => x.id !== id);
    return rs.map((x) => (x.id === id ? { ...x, published } : x));
  });

  // Thumbnail → full player: jump the vertical feed to the tapped clip.
  useEffect(() => {
    if (tab === "stream" && targetId) {
      const el = document.getElementById(`slide-${targetId}`);
      if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
      setTargetId(null);
    }
  }, [tab, targetId]);

  const openReel = (id) => { setTargetId(id); setTab("stream"); };

  // Active-slide gating: observe mounted slides and mark the most-visible one
  // active. Only that slide plays (see Slide's isActive effect).
  useEffect(() => {
    if (tab !== "stream" && tab !== "mine") return;
    const root = scrollRef.current;
    if (!root) return;
    if (!activeId && reels[0]) setActiveId(reels[0].id);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          setActiveId(e.target.getAttribute("data-slide-id"));
        }
      });
    }, { root, threshold: [0.6] });
    root.querySelectorAll("[data-slide-id]").forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reels, tab]);

  const hideReel = (id) => setReels((rs) => {
    const r = rs.find((x) => x.id === id);
    if (r?.src) URL.revokeObjectURL(r.src); // resolve heap leak on removed clip
    return rs.filter((x) => x.id !== id);
  });
  const openSound = (soundId) => {
    // TODO: DB Linkage to mingle/misteck_preview cluster — route to /sounds/:soundId directory
    toast(`Opening Sound Node · /sounds/${soundId}`, { icon: "🎵" });
  };

  return (
    <div className="h-full w-full bg-black relative overflow-hidden" data-testid="reels-feed">
      {/* Header */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center gap-3 px-4 py-3 bg-gradient-to-b from-black/80 to-transparent">
        <button onClick={onBack} data-testid="reels-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white/15 border-2 border-black grid place-items-center backdrop-blur-sm shadow-[2px_2px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} className="text-white" />
        </button>
        <h1 className="font-serif text-2xl tracking-tight" style={{ color: CORAL }} data-testid="reels-title">Explore</h1>
        <button onClick={() => onMakeReel?.()} data-testid="feed-make-reel-btn" aria-label="Make Reel"
          className="ml-auto flex items-center gap-1.5 rounded-full border-2 border-black px-3.5 py-2 font-mono text-[10px] font-black uppercase tracking-[0.16em] text-black shadow-[2px_2px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
          style={{ backgroundColor: CORAL }}>
          <Clapperboard size={15} strokeWidth={2.6} /> Make Reel
        </button>
      </div>

      {loading ? (
        <div className="h-full grid place-items-center" data-testid="reels-loading"><Loader2 size={30} className="text-[#FF4D4D] animate-spin" /></div>
      ) : (tab === "stream" || tab === "mine") ? (
        reels.length === 0 ? (
          <div className="h-full grid place-items-center px-8 text-center"><p className="font-mono text-sm text-white/60" data-testid="reels-empty">{tab === "mine" ? "No reels yet — tap Make Reel to create your first." : "No published reels yet. Open “Mine” to publish one to Explore."}</p></div>
        ) : (
          <div ref={scrollRef} className="h-full w-full overflow-y-scroll snap-y snap-mandatory no-scrollbar" data-testid="reels-scroll">
            {reels.map((r) => (
              <Slide key={r.id} reel={r} onHide={hideReel} onOpenSound={openSound}
                onPublishedChange={handlePublishedChange}
                isActive={activeId === r.id}
                onContinueDraft={() => onMakeReel?.()}
                onUnlock={() => setReloadKey((k) => k + 1)}
                autoScroll={autoScroll} onToggleAutoScroll={() => setAutoScroll((a) => !a)} />
            ))}
          </div>
        )
      ) : (
        <div className="h-full w-full overflow-y-auto no-scrollbar pt-16 pb-24 px-4" data-testid="discover-grid">
          <div className="grid grid-cols-2 gap-3">
            {reels.map((r) => (
              <button key={r.id} onClick={() => openReel(r.id)} data-testid={`discover-card-${r.id}`} className="relative aspect-[9/16] rounded-2xl overflow-hidden border-2 border-black bg-charcoal text-left shadow-[3px_3px_0px_#000]">
                {r.poster && <img src={r.poster} loading="lazy" alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 border-2 border-white/70 grid place-items-center"><Play size={13} className="text-white" fill="currentColor" /></span>
                <span className="absolute bottom-2 left-2 right-2 font-mono text-[10px] text-white/90 leading-tight">{r.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer sub-nav */}
      <div className="absolute bottom-0 inset-x-0 z-30 flex items-center justify-center gap-12 px-6 py-3 bg-gradient-to-t from-black/90 to-transparent" data-testid="reels-subnav">
        <button onClick={() => setTab("stream")} data-testid="subnav-stream" className={["flex flex-col items-center gap-1 transition-colors", tab === "stream" ? "text-[#FF4D4D]" : "text-white/50"].join(" ")}>
          <Clapperboard size={20} strokeWidth={2.2} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Stream</span>
        </button>
        <button onClick={() => setTab("discover")} data-testid="subnav-discover" className={["flex flex-col items-center gap-1 transition-colors", tab === "discover" ? "text-[#FF4D4D]" : "text-white/50"].join(" ")}>
          <Compass size={20} strokeWidth={2.2} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Discover</span>
        </button>
        <button onClick={() => setTab("mine")} data-testid="subnav-mine" className={["flex flex-col items-center gap-1 transition-colors", tab === "mine" ? "text-[#FF4D4D]" : "text-white/50"].join(" ")}>
          <Lock size={20} strokeWidth={2.2} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Mine</span>
        </button>
      </div>
    </div>
  );
}
